import { useQueries, UseQueryOptions } from '@tanstack/react-query';
import { useFetchApi } from '../../../hooks';
import { ApiError, isApiError } from '../../../helper/api';

export type DdsField = {
  verification: string;
  reference: string;
};

// this response is coming from traces and can be changed anytime
type DdsCommodity = {
  position: number;
  descriptors: {
    descriptionOfGoods: string;
    goodsMeasure: {
      netWeight: string;
    };
  };
  hsHeading: string;
  producers: {
    country: string;
    geometryGeojson: string;
  }[];
  speciesInfo?: {
    scientificName: string;
    commonName: string;
  };
};

// this response is coming from traces and can be changed anytime
type DdsResponse = {
  statement: {
    referenceNumber: string;
    activityType: string;
    status: {
      status: string;
      date: string;
    };
    operator: null;
    commodities: DdsCommodity[];
  };
};

type UseFetchDdsDataOptions = UseQueryOptions<DdsResponse, ApiError> & {
  onSuccessCb?: (data: DdsResponse, index: number) => void;
  onErrorCb?: (error: ApiError, index: number) => void;
};

const useFetchDdsData = (
  dds: DdsField[] = [],
  options?: UseFetchDdsDataOptions
) => {
  const fetch = useFetchApi();

  const fetchDdsData = async (
    dds: DdsField,
    index: number,
    options?: UseFetchDdsDataOptions
  ) => {
    try {
      const response = await fetch(
        `dds/fetch-dds-data?verificationNumber=${dds.verification}&referenceNumber=${dds.reference}`
      );
      options?.onSuccessCb?.(response, index);
      return response;
    } catch (error) {
      if (isApiError(error)) {
        options?.onErrorCb?.(error, index);
      }
      return error;
    }
  };

  return useQueries<UseQueryOptions<DdsResponse, ApiError>[]>({
    queries: dds.map((dds, index) => ({
      queryKey: ['dds', dds.verification, dds.reference],
      queryFn: () => fetchDdsData(dds, index, options),
      enabled: !!dds.verification && !!dds.reference,
      retry: false,
      staleTime: 1_000 * 60 * 30, // 30 minutes
      refetchOnWindowFocus: false,
      ...options,
    })),
  });
};

export default useFetchDdsData;
